/**
 * The wire contract.
 *
 * Every byte arriving from the network is parsed through these schemas before
 * any other code sees it. Nothing is trusted: not the relay, not the other
 * participant, not a reconnecting instance of this same app. A message that
 * does not match its schema is dropped and logged, never partially applied.
 *
 * The bounds below are part of the security surface rather than cosmetic. An
 * unbounded chat body is a memory exhaustion vector; an unbounded position is
 * a way to drive the drift controller into nonsense.
 */

import { z } from 'zod';

/**
 * Incremented on any breaking change to the shapes below.
 *
 * The relay refuses a mismatched version at handshake with a clear message,
 * because a subtly incompatible client is far worse than a rejected one: it
 * desynchronises in ways that look like bugs rather than like a version skew.
 */
export const PROTOCOL_VERSION = 2;

/** Longest chat message accepted, in characters. */
export const MAX_CHAT_LENGTH = 2_000;

/** Longest display name accepted. */
export const MAX_DISPLAY_NAME_LENGTH = 40;

/** Upper bound on any media position, in milliseconds. Twenty four hours. */
const MAX_POSITION_MS = 24 * 60 * 60 * 1_000;

/** Bounds on user selectable playback speed. */
const MIN_RATE = 0.25;
const MAX_RATE = 4;

const idSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);

const positionSchema = z.number().finite().min(0).max(MAX_POSITION_MS);

const serverTimeSchema = z.number().finite().nonnegative();

/** What a participant is currently doing, as shown in the presence strip. */
export const presenceStateSchema = z.enum([
  'idle',
  'watching',
  'paused',
  'buffering',
  'reconnecting',
  'finished',
  'disconnected',
]);
export type PresenceState = z.infer<typeof presenceStateSchema>;

export const participantSchema = z.object({
  id: idSchema,
  displayName: z.string().min(1).max(MAX_DISPLAY_NAME_LENGTH),
  presence: presenceStateSchema,
  /** Server time of the last message received from this participant. */
  lastSeenServerMs: serverTimeSchema,
});
export type Participant = z.infer<typeof participantSchema>;

/**
 * The serialised form of the playback line described in @cinesync/sync-core.
 *
 * Deliberately declared here rather than imported, so that sync-core stays
 * dependency free and the wire format can evolve independently of the
 * in-memory representation.
 */
export const playbackStateSchema = z.object({
  anchorPositionMs: positionSchema,
  anchorServerMs: serverTimeSchema,
  rate: z.number().finite().min(MIN_RATE).max(MAX_RATE),
  paused: z.boolean(),
  startAtServerMs: serverTimeSchema.nullable(),
  durationMs: positionSchema.nullable(),
});
export type WirePlaybackState = z.infer<typeof playbackStateSchema>;

/**
 * A fingerprint of the media a participant has open.
 *
 * Because both sides play their own copy of the file, a room is only
 * meaningful if those copies match. Comparing size and duration alongside a
 * hash of the opening bytes catches the realistic mismatches, two different
 * rips or two different cuts, without hashing gigabytes.
 */
export const mediaFingerprintSchema = z.object({
  /** Hash of a bounded sample of the file, hex encoded. */
  sampleHash: z.string().length(64).regex(/^[0-9a-f]+$/),
  sizeBytes: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  durationMs: positionSchema,
  /** Shown when a mismatch needs explaining to a person. */
  displayName: z.string().min(1).max(255),
});
export type MediaFingerprint = z.infer<typeof mediaFingerprintSchema>;

/** Longest media URL accepted on the wire. */
export const MAX_SOURCE_URL_LENGTH = 2_048;

/**
 * What the room is watching.
 *
 * This exists because the two people in a room do not always both have the
 * film. One of them finds it, and the other should not have to be told a name
 * over the phone and go looking. Announcing the source makes the second copy
 * open by itself.
 *
 * Only `http:` and `https:` are accepted, and that restriction is the whole
 * security story of this message. The URL is handed straight to a media
 * element, so allowing `javascript:` would turn the other participant into a
 * script injection, and `blob:` or `file:` would name something that only
 * exists on the sender's machine.
 */
export const roomSourceSchema = z.object({
  url: z
    .string()
    .min(1)
    .max(MAX_SOURCE_URL_LENGTH)
    .refine((value) => {
      try {
        const protocol = new URL(value).protocol;
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    }, 'must be an http or https URL'),
  title: z.string().min(1).max(255),
  mimeType: z.string().max(255).nullable(),
  durationMs: positionSchema.nullable(),
});
export type RoomSource = z.infer<typeof roomSourceSchema>;

/**
 * An agreed moment to press play, in server time.
 *
 * Used for films this app cannot open itself. It carries no playback line
 * because there is nothing to drive: the app is counting two people in, not
 * controlling their players.
 */
export const countdownSchema = z.object({
  startAtServerMs: serverTimeSchema,
  fromPositionMs: positionSchema,
});
export type Countdown = z.infer<typeof countdownSchema>;

/** One retained chat message, as replayed to someone joining late. */
export const chatHistoryEntrySchema = z.object({
  id: idSchema,
  authorId: idSchema,
  body: z.string().min(1).max(MAX_CHAT_LENGTH),
  atPositionMs: positionSchema.nullable(),
  serverTimeMs: serverTimeSchema,
});
export type ChatHistoryEntry = z.infer<typeof chatHistoryEntrySchema>;

/* -------------------------------------------------------------------------
 * Client to relay
 * ------------------------------------------------------------------------- */

export const clientMessageSchema = z.discriminatedUnion('type', [
  /** First message on a fresh socket. Establishes version and identity. */
  z.object({
    type: z.literal('hello'),
    protocolVersion: z.number().int(),
    displayName: z.string().min(1).max(MAX_DISPLAY_NAME_LENGTH),
    /** Present when resuming an interrupted session rather than joining new. */
    resumeParticipantId: idSchema.optional(),
    media: mediaFingerprintSchema.nullable(),
  }),

  /** Clock probe. `t0` comes back untouched so the client can pair the reply. */
  z.object({
    type: z.literal('ping'),
    t0: z.number().finite(),
  }),

  /**
   * A request to change what the room is doing.
   *
   * Called an intent rather than a command because the relay, not the client,
   * decides the outcome. Two intents arriving together are ordered by the
   * relay and the loser is simply superseded.
   */
  z.object({
    type: z.literal('intent'),
    intent: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('play'), fromPositionMs: positionSchema.optional() }),
      z.object({ kind: z.literal('pause'), atPositionMs: positionSchema }),
      z.object({ kind: z.literal('seek'), toPositionMs: positionSchema }),
      z.object({ kind: z.literal('rate'), rate: z.number().finite().min(MIN_RATE).max(MAX_RATE) }),
    ]),
  }),

  /** Reports that this client has buffered enough to honour a scheduled start. */
  z.object({
    type: z.literal('ready'),
    positionMs: positionSchema,
    bufferedAheadMs: z.number().finite().nonnegative().max(MAX_POSITION_MS),
  }),

  z.object({
    type: z.literal('presence'),
    state: presenceStateSchema,
  }),

  z.object({
    type: z.literal('chat'),
    /** Client generated, so the sender can reconcile its optimistic render. */
    localId: idSchema,
    body: z.string().min(1).max(MAX_CHAT_LENGTH),
    /** Playback position this message refers to, for "jump to this moment". */
    atPositionMs: positionSchema.nullable(),
  }),

  z.object({ type: z.literal('typing'), active: z.boolean() }),

  /**
   * A revised media fingerprint.
   *
   * `hello` carries one too, but the ordinary order of events is to join a
   * room and then open the film, so without this the mismatch check would only
   * ever run for someone who happened to open the film first.
   */
  z.object({
    type: z.literal('media'),
    media: mediaFingerprintSchema.nullable(),
  }),

  /** Announce what the room is watching, or `null` to clear it. */
  z.object({
    type: z.literal('source'),
    source: roomSourceSchema.nullable(),
  }),

  /** Schedule, or cancel, an agreed moment to press play. */
  z.object({
    type: z.literal('countdown'),
    countdown: countdownSchema.nullable(),
  }),

  z.object({ type: z.literal('bye') }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

/* -------------------------------------------------------------------------
 * Relay to client
 * ------------------------------------------------------------------------- */

export const serverErrorCodeSchema = z.enum([
  'version-mismatch',
  'room-not-found',
  'room-full',
  'rate-limited',
  'malformed',
  'internal',
]);
export type ServerErrorCode = z.infer<typeof serverErrorCodeSchema>;

export const serverMessageSchema = z.discriminatedUnion('type', [
  /** Accepted. Carries everything needed to render the room immediately. */
  z.object({
    type: z.literal('welcome'),
    protocolVersion: z.number().int(),
    roomCode: z.string(),
    participantId: idSchema,
    participants: z.array(participantSchema).max(16),
    serverTimeMs: serverTimeSchema,
    seq: z.number().int().nonnegative(),
    playback: playbackStateSchema,
    /** Retained so a late joiner sees the conversation already in progress. */
    chat: z.array(chatHistoryEntrySchema).max(200),
    /** What the room is watching, if anyone has said. */
    source: roomSourceSchema.nullable(),
    countdown: countdownSchema.nullable(),
  }),

  /** Clock probe reply. `t1` and `t2` bracket the relay's handling time. */
  z.object({
    type: z.literal('pong'),
    t0: z.number().finite(),
    t1: serverTimeSchema,
    t2: serverTimeSchema,
  }),

  /**
   * The room's playback state changed.
   *
   * `seq` is assigned by the relay and increases monotonically. A client that
   * receives a lower sequence than it has already applied discards it, which
   * is what makes out of order delivery harmless.
   */
  z.object({
    type: z.literal('playback'),
    seq: z.number().int().nonnegative(),
    playback: playbackStateSchema,
    /** Who caused this, so the interface can say "your brother paused". */
    actorId: idSchema.nullable(),
    serverTimeMs: serverTimeSchema,
  }),

  z.object({
    type: z.literal('participants'),
    participants: z.array(participantSchema).max(16),
    serverTimeMs: serverTimeSchema,
  }),

  z.object({
    type: z.literal('chat'),
    id: idSchema,
    localId: idSchema.nullable(),
    authorId: idSchema,
    body: z.string().min(1).max(MAX_CHAT_LENGTH),
    atPositionMs: positionSchema.nullable(),
    serverTimeMs: serverTimeSchema,
  }),

  z.object({
    type: z.literal('typing'),
    participantId: idSchema,
    active: z.boolean(),
  }),

  /** A participant's media does not match the room's. */
  z.object({
    type: z.literal('media-mismatch'),
    participantId: idSchema,
    theirs: mediaFingerprintSchema.nullable(),
  }),

  /** The room's source changed. Clients open it without being asked. */
  z.object({
    type: z.literal('source'),
    source: roomSourceSchema.nullable(),
    actorId: idSchema.nullable(),
    serverTimeMs: serverTimeSchema,
  }),

  z.object({
    type: z.literal('countdown'),
    countdown: countdownSchema.nullable(),
    actorId: idSchema.nullable(),
    serverTimeMs: serverTimeSchema,
  }),

  z.object({
    type: z.literal('error'),
    code: serverErrorCodeSchema,
    message: z.string().max(500),
    /** Whether the socket is being closed as a result. */
    fatal: z.boolean(),
  }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

/* -------------------------------------------------------------------------
 * Parsing
 * ------------------------------------------------------------------------- */

export type ParseResult<T> =
  | { readonly ok: true; readonly message: T }
  | { readonly ok: false; readonly reason: string };

/**
 * Parse a raw frame from the network.
 *
 * Returns a result rather than throwing, because a malformed frame is an
 * expected condition on a public endpoint, not an exceptional one. Callers
 * drop it, count it, and carry on serving the socket.
 */
function parseFrame<T>(schema: z.ZodType<T>, raw: unknown): ParseResult<T> {
  let candidate: unknown = raw;

  if (typeof raw === 'string') {
    // Guard before parsing rather than after: a 50 MB frame should be refused
    // without ever being turned into objects.
    if (raw.length > 64 * 1024) return { ok: false, reason: 'frame too large' };
    try {
      candidate = JSON.parse(raw);
    } catch {
      return { ok: false, reason: 'not valid JSON' };
    }
  }

  const result = schema.safeParse(candidate);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    return { ok: false, reason: path ? `${path}: ${first?.message}` : (first?.message ?? 'invalid') };
  }
  return { ok: true, message: result.data };
}

export const parseClientMessage = (raw: unknown): ParseResult<ClientMessage> =>
  parseFrame(clientMessageSchema, raw);

export const parseServerMessage = (raw: unknown): ParseResult<ServerMessage> =>
  parseFrame(serverMessageSchema, raw);

/** Serialise for the wire. Centralised so framing can change in one place. */
export const encodeMessage = (message: ClientMessage | ServerMessage): string =>
  JSON.stringify(message);
